import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { 
    setCompany, 
    setDoctorId, 
    setRole, 
    setUserId,
    setBankName,
    setBik,
    setCheckingAccount,
    setCorrespondentAccount,
    setInn,
    setKpp,
    setLanguage
} from "./loginSlice";
import Logo from "../Common/Logo";
import InstructionBanner from "../Common/InstructionBanner";
import { doForgetPassword, doLogin } from "./loginApi";
import "./Login.css";

// Валидация формы
const schema = yup.object().shape({
    email: yup.string().email("Введите корректный email").required("Обязательное поле"),
    password: yup.string().min(6, "Минимум 6 символов").required("Обязательное поле"),
});

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    
    // Получаем ID реферера из URL параметров
    const referrerId = searchParams.get('ref_id') || searchParams.get('referrer_id') || null;
    console.log('referrerId', referrerId)
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
    } = useForm({ resolver: yupResolver(schema) });

    // Загружаем сохранённые данные из localStorage
    useEffect(() => {
        const savedEmail = localStorage.getItem("dental_email");
        const savedPassword = localStorage.getItem("dental_password");
        if (savedEmail) setValue("email", savedEmail);
        if (savedPassword) setValue("password", savedPassword);
    }, [setValue]);

    // Обработка логина
    const onSubmit = async (data) => {
        try {
            const loginData = {
                ...data,
                ...(referrerId && { referrer_id: referrerId })
            };
            const response = await doLogin(loginData);
            if (response.res) {
                if (response.user.is_doctor) {
                    dispatch(setRole("doctor"));
                    dispatch(setDoctorId(response.user.doctor_id));
                } else {
                    dispatch(setRole("user"));
                }

                dispatch(setCompany(response.user.company_name || ''));
                dispatch(setUserId(response.user.user_id));
                
                // Загружаем банковские реквизиты, если они приходят с сервера
                if (response.user.bank_name !== undefined) {
                    dispatch(setBankName(response.user.bank_name || ''));
                }
                if (response.user.bik !== undefined) {
                    dispatch(setBik(response.user.bik || ''));
                }
                if (response.user.checking_account !== undefined) {
                    dispatch(setCheckingAccount(response.user.checking_account || ''));
                }
                if (response.user.correspondent_account !== undefined) {
                    dispatch(setCorrespondentAccount(response.user.correspondent_account || ''));
                }
                if (response.user.inn !== undefined) {
                    dispatch(setInn(response.user.inn || ''));
                }
                if (response.user.kpp !== undefined) {
                    dispatch(setKpp(response.user.kpp || ''));
                }
                if (response.user.language) {
                    dispatch(setLanguage(response.user.language));
                }

                localStorage.setItem("dental_email", data.email);
                localStorage.setItem("dental_password", data.password);

                navigate("/dentapp/lab");
            } else {
                if (response.message.includes("gd23.ru")) {
                    if (window.confirm(`${response.message}\nПерейти на gd23.ru?`)) {
                        window.open("https://gd23.ru", "_blank");
                    }
                } else {
                    alert(response.message);
                }
            }
        } catch (err) {
            console.error("Ошибка авторизации:", err);
            alert("Не удалось выполнить вход. Попробуйте позже.");
        }
    };

    // Восстановление пароля
    const handleForgotPassword = async () => {
        const email = (document.querySelector("#email")?.value ?? "").trim();

        if (!email) {
            alert("Введите email для восстановления пароля");
            return;
        }

        try {
            const res = await doForgetPassword({ email });
            if (res.res) {
                alert(`Инструкции отправлены на ${email}`);
            } else {
                alert("Не удалось отправить инструкции по восстановлению пароля");
            }
        } catch (err) {
            alert("Ошибка восстановления пароля");
        }
    };

    return (
        <div className="login_container">
            <div className="svg_logo mt20">
                <Logo />
            </div>

            <h1 className="login_title">Приветствую!</h1>
            <h2 className="login_title mt20">В DENTAL LABORATORY</h2>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col space-y-4 mt20">
                <div className="cntr_input">
                    <label className="label_email">Введите EMAIL</label>
                    <input
                        id="email"
                        type="email"
                        placeholder="Email"
                        {...register("email")}
                        className="input_login"
                    />
                    {errors.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
                </div>

                <div className="cntr_input ">
                    <label className="label_email">Пароль</label>
                    <div className="eye_input_cntr">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Пароль"
                            {...register("password")}
                            className="input_login"
                        />
                        <span
                            className="eyeButton"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? "🙈" : "👁️"}
                        </span>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs">{errors.password.message}</p>}
                </div>
                <div className="cntr_input mt20">

                    <button type="submit" disabled={isSubmitting} className="login_button">
                        <span className="button_label">{isSubmitting ? "Вход..." : "Войти"}</span>
                    </button>

                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="label_email mt20"
                        style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}
                    >
                        Забыли пароль?
                    </button>

                    <a
                        href="tel:+79184339086"
                        className="label_email mt20"
                        style={{
                            color: "inherit",
                            textAlign: "center",
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                    >
                        Техподдержка
                    </a>
                </div>
            </form>

            <InstructionBanner />
        </div>
    );
};

export default Login;
